import { useState } from 'react'
import { Button } from 'components/atoms/button'
import { PieImage } from 'components/common'
import { ContentModal } from 'components/organisms/modals/content-modal'
import { buildReceivedTicketCopy } from '../received-ticket'

export interface ReceivedTicketModalProps {
	open: boolean
	setOpen: (open: boolean) => void
	ticketName: string
	/** Paid tickets read as "ticket", free ones as "pass" */
	isPaid: boolean
	eventName: string
	/** The event's cover image, so the recipient sees at once which event the ticket is for; a placeholder shows without one */
	eventImage?: string | null
}

// A ticket shared with the viewer moves to them with no accept step, so this only tells them it arrived: the event
// page stays rendered behind it (where the ticket now lives) and "Done" just closes it. Rendered by the modal manager,
// which sits ABOVE the <Router>, so it must not use react-router hooks.
export function ReceivedTicketModal({
	open,
	setOpen,
	ticketName,
	isPaid,
	eventName,
	eventImage,
}: Readonly<ReceivedTicketModalProps>) {
	const { title, body } = buildReceivedTicketCopy({ ticketName, isPaid, eventName })

	return (
		<ContentModal open={open} setOpen={setOpen} hideCloseButton title={title}>
			<div className="relative flex flex-col items-center space-y-5">
				{/* Decorative: the event's name is in the text below. Fixed size, so nothing moves while it loads */}
				<PieImage
					className="h-32 w-32 flex-none overflow-hidden rounded-2xl border border-translucent-plus-border"
					images={[eventImage]}
					alt=""
				/>
				<p className="max-w-[304px] text-center text-secondary-text">{body}</p>
				<Button variant="primary" fullWidth onClick={() => setOpen(false)}>
					Done
				</Button>
			</div>
		</ContentModal>
	)
}

// ---------------------------------------------------------------------------
// Modal Manager integration
// ---------------------------------------------------------------------------

export interface ReceivedTicketModalManagedProps {
	ticketName: string
	isPaid: boolean
	eventName: string
	eventImage?: string | null
}

declare module 'components/organisms/modals/modal-registry' {
	interface ModalRegistry {
		'received-ticket': ReceivedTicketModalManagedProps
	}
}

export function ManagedReceivedTicketModal({
	onClose,
	...rest
}: ReceivedTicketModalManagedProps & { onClose: () => void }) {
	const [open, setOpen] = useState(true)

	const handleClose = () => {
		setOpen(false)
		setTimeout(onClose, 200)
	}

	return (
		<ReceivedTicketModal
			open={open}
			setOpen={(value: boolean) => {
				if (!value) handleClose()
			}}
			{...rest}
		/>
	)
}
